

import { cn } from "@/lib/utils";

interface CircularProgressProps {
  progress: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

const CircularProgress = ({
  progress,
  size = 525,
  strokeWidth = 15,
  className,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  
  // Reverse the progress calculation to make it start from full circle and shrink
  // as progress increases (100% progress = empty circle)
  const dash = ((100 - progress) * circumference) / 100;

  // Multiplier for the active animated ring thickness (was 0.5, now 0.35 for thinner ring)
  const activeStrokeWidth = strokeWidth * 0.35;

  return (
    <svg
      className={cn("transform -rotate-90", className)}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
    >
      {/* Filter for inner shadow */}
      <defs>
        <filter id="innerShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur"/>
          <feOffset dx="0" dy="0" result="offsetBlur"/>
          <feComposite in="SourceAlpha" in2="offsetBlur" operator="out" result="innerShadow"/>
          <feFlood floodColor="rgba(0,0,0,0.3)" floodOpacity="1" result="color"/>
          <feComposite in="color" in2="innerShadow" operator="in" result="shadow"/>
          <feComposite in="shadow" in2="SourceAlpha" operator="in" result="finalShadow"/>
          <feMerge>
            <feMergeNode in="SourceGraphic"/>
            <feMergeNode in="finalShadow"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Background static ring */}
      <circle
        className="stroke-white/30 fill-none"
        cx={size / 2}
        cy={size / 2}
        r={radius}
        strokeWidth={strokeWidth}
        filter="url(#innerShadow)"
      />

      {/* Active animated ring - thinner than before (35% of background ring) */}
      <circle
        className="stroke-white fill-none transition-all duration-1000 ease-linear"
        cx={size / 2}
        cy={size / 2}
        r={radius}
        strokeWidth={activeStrokeWidth}
        strokeDasharray={`${dash} ${circumference}`}
        strokeLinecap="round"
        style={{
          transitionProperty: 'stroke-dasharray',
        }}
      />
    </svg>
  );
};

export default CircularProgress;
